/**
 * PublicCertificate — no-auth verification page.
 *
 * Anyone with the link can view the certificate. Revoked certificates return
 * 410 Gone and we show a friendly explanation.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Copy, Check, Linkedin } from "lucide-react";
import CertificateView from "@/components/CertificateView";
import { API } from "@/lib/api";
import { toast } from "sonner";

export default function PublicCertificate() {
    const { certId } = useParams();
    const [state, setState] = useState({ loading: true, cert: null, error: null });
    const cardRef = useRef(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/certificates/public/${certId}`);
                if (res.status === 410) {
                    setState({ loading: false, cert: null, error: "Ce certificat a été révoqué." });
                    return;
                }
                if (!res.ok) {
                    setState({ loading: false, cert: null, error: "Certificat introuvable." });
                    return;
                }
                const data = await res.json();
                setState({ loading: false, cert: data.certificate, error: null });
            } catch {
                setState({ loading: false, cert: null, error: "Impossible de charger le certificat." });
            }
        })();
    }, [certId]);

    async function handleCopyLink() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            toast.success("Lien copié.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Impossible de copier le lien.");
        }
    }

    if (state.loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }

    if (state.error) {
        return (
            <div className="min-h-screen bg-paper flex flex-col items-center justify-center p-6" data-testid="public-certificate-error">
                <p className="font-serif text-2xl">{state.error}</p>
                <Link to="/" className="mt-4 text-sm text-terracotta hover:underline">
                    Retour à Lexia School
                </Link>
            </div>
        );
    }

    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;

    return (
        <div className="min-h-screen bg-paper" data-testid="public-certificate-page">
            <header className="border-b border-lexiBorder bg-white print:hidden">
                <div className="max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4">
                    <Link to="/" className="font-serif text-lg" data-testid="public-cert-brand">
                        Lexia School
                    </Link>
                    <Link to="/register" className="text-sm text-terracotta hover:underline">
                        Créer mon compte →
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 print:p-0 print:max-w-none">
                <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3 print:hidden">
                    Vérification publique
                </p>
                <h1 className="font-serif text-3xl md:text-4xl leading-tight print:hidden">
                    Certificat authentifié — Lexia School.
                </h1>
                <p className="mt-3 text-sm text-inkSoft max-w-2xl inline-flex items-start gap-2 print:hidden">
                    <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald shrink-0" />
                    Ce lien a été émis directement par Lexia School. Le contenu est figé au moment de la
                    complétion du parcours et ne peut pas être modifié après émission.
                </p>

                <div className="mt-8">
                    <CertificateView ref={cardRef} certificate={state.cert} />
                </div>

                <div className="mt-6 flex flex-wrap gap-2 print:hidden">
                    <button
                        onClick={handleCopyLink}
                        data-testid="public-cert-copy-link"
                        className="inline-flex items-center gap-2 bg-paperAlt border border-lexiBorder hover:bg-paper rounded-full px-4 py-2 text-sm transition-colors"
                    >
                        {copied ? <Check className="w-4 h-4 text-emerald" /> : <Copy className="w-4 h-4" />}
                        {copied ? "Lien copié" : "Copier le lien"}
                    </button>
                    <a
                        href={linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="public-cert-linkedin"
                        className="inline-flex items-center gap-2 bg-[#0A66C2] text-white hover:bg-[#004182] rounded-full px-4 py-2 text-sm transition-colors"
                    >
                        <Linkedin className="w-4 h-4" />
                        Partager sur LinkedIn
                    </a>
                </div>

                <div className="mt-10 text-center print:hidden">
                    <p className="text-sm text-inkSoft">Envie d&apos;obtenir un certificat comme celui-ci ?</p>
                    <Link
                        to="/register"
                        data-testid="public-cert-cta"
                        className="inline-flex items-center gap-2 mt-3 bg-terracotta text-white hover:bg-terracottaDark rounded-full px-5 py-2.5 text-sm"
                    >
                        Découvrir Lexia School
                    </Link>
                </div>
            </main>
        </div>
    );
}
